import type { StudioPathLike } from '@sanity/client/csm';
import type { SanityReference} from '@sanity/client/stega';
import { stegaClean } from '@sanity/client/stega';
import type {
    ImageUrlBuilder,
    SanityAsset,
    SanityClientLike,
    SanityImageCrop,
    SanityImageHotspot,
    SanityProjectDetails} from '@sanity/image-url';
import {
    createImageUrlBuilder
} from '@sanity/image-url';
import cn from 'classnames';
import type { DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import { preloadImage } from '../../utils/HeaderContext';
import useDataAttribute from '../infrastructure/DataAttributeContext';
import { useImageUrlBuilder } from '../infrastructure/ImageUrlBuilderContext';

export const imageWithMetadataGroq = /* groq */ `{
    ...,
    asset-> {
        _id,
        url,
        extension,
        altText,
        creditLine,
        'backgroundColor': metadata.palette.dominant.background,
        'palette': metadata.palette,
        'lqip': metadata.lqip
    }
}`;

export interface ImageWithMetadata {
    asset?: (SanityReference | SanityAsset) & {
        title?: string;
        altText?: string;
        creditLine?: string;
        extension?: string;
        backgroundColor?: string;
    };
    crop?: SanityImageCrop;
    hotspot?: SanityImageHotspot;
    description?: string;
}

export interface Props extends Omit<DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>, 'loading'> {
    image?: ImageWithMetadata | string;
    /**
     * The sizes this image will have on the page for different media queries.
     *
     * For example: `(min-width: 1440px) 1440px, 100vw`
     * * When the viewport is above 1440px the image will be 1440px wide,
     * * when the viewport is below that it will be the size of the viewport
     *
     */
    sizes?: string;
    /**
     * A list of widths in pixels that the image might be loaded in.
     * You should specify at least any width specified in sizes and double that, for
     * 2x dpr devices
     */
    widths: number[];
    aspectRatio?: number;
    lazy?: boolean;
}

export function imageWithMetadataFromUrl(url: string): { projectDetails: SanityProjectDetails; image: ImageWithMetadata } {
    const { pathname, searchParams } = new URL(url);
    const [, projectId, dataset, id, width, height, extension] = /images\/(\w+)\/(\w+)\/(\w+)-(\d+)x(\d+)\.(\w+)$/.exec(pathname) ?? [];
    const rect = searchParams
        .get('rect')
        ?.split(',')
        .map((x) => parseInt(x, 10));

    return {
        projectDetails: {
            projectId,
            dataset,
        },
        image: {
            asset: {
                _id: `image-${id}-${width}x${height}-${extension}`,
            },
            crop: rect
                ? {
                      _type: 'sanity.imageCrop',
                      left: rect[0] / parseInt(width, 10),
                      top: rect[1] / parseInt(height, 10),
                      right: 1 - (rect[0] + rect[2]) / parseInt(width, 10),
                      bottom: 1 - (rect[1] + rect[3]) / parseInt(height, 10),
                  }
                : undefined,
        },
    };
}

export default function SanityImage({ image, sizes, widths, aspectRatio, style, lazy, ...props }: Props) {
    let projectDetails: SanityProjectDetails | undefined = undefined;
    if (typeof image === 'string') {
        const result = imageWithMetadataFromUrl(image);
        image = result.image;
        projectDetails = result.projectDetails;
    }
    const builder = useImageUrlBuilder(projectDetails);
    if (!image?.asset) return null;
    const src = builder.image(image);
    const isSvg = stegaClean(image.asset.extension) === 'svg';
    if (isSvg) {
        const url = src.url();
        if (props.fetchPriority === 'high') {
            preloadImage(url, 'image/svg+xml', 'high');
        }

        return (
            <img
                src={url}
                style={{
                    aspectRatio,
                    ...style,
                }}
                alt={stegaClean(image.asset.altText)}
                {...props}
                loading={lazy ? 'lazy' : undefined}
            />
        );
    } else {
        return (
            <img
                srcSet={toSrcSet(src, widths, aspectRatio)}
                sizes={sizes}
                style={{
                    aspectRatio,
                    ...style,
                }}
                alt={stegaClean(image.asset.altText)}
                {...props}
                loading={lazy ? 'lazy' : undefined}
            />
        );
    }
}

export function CaptionedSanityImage({ studioPath, float, ...props }: Props & { studioPath?: StudioPathLike; float?: 'left' | 'right' }) {
    const attr = useDataAttribute();
    if (typeof props.image !== 'object' || !props.image?.asset) return null;
    return (
        <figure data-sanity={studioPath && attr?.(studioPath)} className={cn({ float }, float)}>
            <SanityImage {...props} />
            <Caption creditLine={props.image.asset.creditLine} description={props.image.description} />
        </figure>
    );
}

function Caption({ creditLine, description }: { creditLine?: string; description?: string }) {
    if (!creditLine && !description) return null;
    return (
        <figcaption>
            {description} <CreditLine creditLine={creditLine} />
        </figcaption>
    );
}

const bannerImageWidths = [320, 640, 1024, 1288];
export function BannerImage({ image, aspectRatio }: { image?: ImageWithMetadata; aspectRatio?: number }) {
    return (
        <CaptionedSanityImage
            image={image}
            widths={bannerImageWidths}
            aspectRatio={aspectRatio}
            sizes="(min-width: 1288px) 1288px, 100vw"
            studioPath="bannerImage"
        />
    );
}

export function getImageUrl(
    image: ImageWithMetadata,
    width: number,
    aspectRatio?: number,
    builder?: ImageUrlBuilder | SanityClientLike | SanityProjectDetails,
) {
    const isSvg = stegaClean(image.asset?.extension) === 'svg';

    builder = builder ? ('url' in builder ? builder : createImageUrlBuilder(builder)) : useImageUrlBuilder();

    builder = builder.image(image);

    if (!isSvg) {
        builder = builder.auto('format').width(width);
        if (aspectRatio) {
            builder = builder.height(Math.floor(width / aspectRatio)).fit('crop');
        }
    }

    return builder.url();
}

function toSrcSet(src: ImageUrlBuilder, widths: number[], aspectRatio?: number) {
    return widths
        .map((w) => `${(aspectRatio ? src.height(Math.floor(w / aspectRatio)).fit('crop') : src).width(w).auto('format').url()} ${w}w`)
        .join(', ');
}

export function CreditLine({ creditLine }: { creditLine: string | undefined }) {
    if (!creditLine) return null;

    return (
        <>
            <strong>Foto:</strong> {creditLine}
        </>
    );
}
